import { skipToken } from "@reduxjs/toolkit/query";
import {
  type Assessment,
  sortAssessmentFields,
} from "@repo/app-types/entities/assessment";
import {
  buildInterviewSectionAssessmentCustomFieldName,
  checkIsCustomFieldDefinitionImportanceMustHave,
} from "@repo/app-types/entities/customField";
import type { InterviewSectionDefinition } from "@repo/app-types/entities/interviewSection/definition";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import type { Project } from "@repo/app-types/entities/project";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import {
  assertUnreachable,
  buildInterviewContactDisplayNames,
  getIsRecruitingOrTrainingProject,
} from "@repo/app-types/helpers/utilities";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconHelpCircle,
  IconMail,
  IconMessageCircle,
  IconPencil,
} from "@tabler/icons-react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../api/entities/interviewSectionDefinition.js";
import type { InterviewWithContactAndActivities } from "../../app/admin_client_types.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { selectAssessmentById } from "../assessments/assessmentsSlice.js";
import { renderCustomFieldDefinitionImportanceMustHaveIcon } from "../assessments/helpers.js";
import { LabeledAssessmentScore } from "../insights/assessmentScore.js";
import { SentimentScore } from "../insights/sentimentScore.js";
import { PaginationControls } from "../miscellaneous/paginationControls.js";
import { selectPhoneNumberAndExtensionForProject } from "../phoneNumbers/phoneNumbersSlice.js";
import { InterviewDecisionIndicator } from "./interviewDecisionIndicator.js";
import { InterviewQuoteMatch } from "./interviewQuoteMatch.js";
import { InterviewAssessmentRating } from "./interviewSlideover/interviewAssessmentRating.js";
import { InterviewSummary } from "./interviewSlideover/interviewSummary.js";
import { UnansweredQuestionsBanner } from "./interviewSlideover/unansweredQuestionsBanner.js";
import {
  fetchNonEmptyInterviewsForProject,
  selectInterviewIdToMatchingFragmentId,
  selectInterviewsParameters,
  selectTotalInterviewPages,
} from "./interviewsSlice.js";

type InterviewsTableRow = InterviewWithContactAndActivities;

export interface InterviewsTableProps {
  interviews: readonly InterviewWithContactAndActivities[];
  isLoading: boolean;
  onInterviewClick: (
    interviewId: string,
    matchingTranscriptFragmentId?: string,
  ) => void;
  project: Project;
}

const placeholder = <span className="text-gray-400">--</span>;

export const InterviewsTable: FC<InterviewsTableProps> = (props) => {
  const navigate = useNavigate();
  const { interviews, onInterviewClick, project } = props;
  const isRecruitingOrTrainingProject =
    getIsRecruitingOrTrainingProject(project);
  const dispatch = useAppDispatch();
  const interviewsParameters = useAppSelector(selectInterviewsParameters);
  const totalPages = useAppSelector(selectTotalInterviewPages);
  const interviewIdToMatchingFragmentId = useAppSelector(
    selectInterviewIdToMatchingFragmentId,
  );
  const assessment = useAppSelector(
    selectAssessmentById(
      getProjectAndJobTypeProperty(project, JobTypeProperty.AssessmentId),
    ),
  );
  const phoneNumberAndExtension = useAppSelector(
    selectPhoneNumberAndExtensionForProject(project.id),
  );
  const doesProjectHavePhoneNumber = !!phoneNumberAndExtension?.phoneNumber;

  const jobTypeId = project.job_type?.id;
  const { data: interviewSectionDefinitions } =
    useAllInterviewSectionDefinitionsForJobTypeQuery(
      // Skip the query if there's no job type ID
      jobTypeId ? { jobTypeId } : skipToken,
    );

  const getAssessmentColumns = (): ColumnDef<InterviewsTableRow>[] => {
    // For non recruiting projects we just show the contact and summary
    if (!isRecruitingOrTrainingProject) {
      return [];
    }

    // Initialize the columns array
    const assessmentColumns: ColumnDef<InterviewsTableRow>[] =
      collectStandardAssessmentColumns(assessment, interviewSectionDefinitions);

    // If we didn't end up with any columns, show a placeholder.
    if (assessmentColumns.length === 0) {
      assessmentColumns.push({
        accessorFn: () => (
          <span className="text-gray-400">No assessment criteria.</span>
        ),
        id: "assessment_placeholder",
        header: () => (
          <div className="flex flex-row gap-2">
            <span>Assessment fields</span>
            <IconButton
              icon={<IconPencil className="h-3 w-3" />}
              onClick={() => {
                navigate(buildProjectUrlPath(project.id));
              }}
              variant={ButtonVariant.Tertiary}
            />
          </div>
        ),
        enableSorting: false,
      });
    }

    return assessmentColumns;
  };

  // Define table columns. Sortable by email, status, and started_at but not names.
  const coreColumns: ColumnDef<InterviewsTableRow>[] = [
    {
      accessorFn: (row) => {
        const {
          primaryDisplayName,
          secondaryDisplayName,
          tertiaryDisplayName,
        } = buildInterviewContactDisplayNames(
          row.contact,
          row.contact.account ? row.contact.account.name : undefined,
        );

        return (
          <div className="truncate">
            <div className="flex items-center gap-1">
              <span className="truncate">{primaryDisplayName}</span>
              {renderInterviewTimeIndicator(row)}
              <InterviewDecisionIndicator decision={row.decision} />
              {renderInterviewStatusIndicator(row, doesProjectHavePhoneNumber)}
              {maybeRenderUnansweredQuestionsIndicator(row)}
            </div>
            {secondaryDisplayName ? (
              <div className="truncate text-gray-600">
                {secondaryDisplayName}
              </div>
            ) : null}
            {tertiaryDisplayName ? (
              <div className="truncate text-gray-600">
                {tertiaryDisplayName}
              </div>
            ) : null}
          </div>
        );
      },
      id: "email",
      header: () => <span>Contact</span>,
      enableSorting: false,
    },
    {
      accessorFn: (row) => {
        // For recruiting projects we show the assessment score
        if (isRecruitingOrTrainingProject) {
          if (!row.insights?.assessment_score) {
            return placeholder;
          }

          return (
            <div className="flex flex-col gap-2 text-wrap">
              <TooltipTrigger>
                <TooltipHoverTarget className="flex-col items-start gap-1">
                  <LabeledAssessmentScore
                    score={
                      row.insights.assessment_score_measurement ??
                      row.insights.assessment_score
                    }
                  />
                </TooltipHoverTarget>
                <SimpleTooltip>
                  <InterviewSummary insights={row.insights} isToolTipMode />
                </SimpleTooltip>
              </TooltipTrigger>
              <InterviewQuoteMatch interview={row} />
            </div>
          );
        }

        // For non recruiting projects we show the sentiment score and interview summary
        return (
          <div className="flex flex-col space-y-2 text-wrap">
            {row.insights?.sentiment_score ? (
              <div>
                <SentimentScore score={row.insights.sentiment_score} />
              </div>
            ) : null}
            {row.insights ? <InterviewSummary insights={row.insights} /> : null}
            <InterviewQuoteMatch interview={row} />
          </div>
        );
      },
      id: "summary",
      header: () => (
        <span>
          {isRecruitingOrTrainingProject
            ? "Overall score"
            : "Sentiment & summary"}
        </span>
      ),
      enableSorting: false,
    },
    ...getAssessmentColumns(),
  ];

  const columns = useMemo(() => coreColumns, [coreColumns]);

  const readOnlyColumn: Partial<ColumnDef<InterviewsTableRow>> = {
    cell: ({ getValue }) => {
      return (
        <div className="px-3 py-2 text-left text-sm">
          {getValue() as string}
        </div>
      );
    },
  };

  // Keep a stable data reference for TanStack Table while still satisfying its mutable array type.
  const tableData = useMemo(() => [...interviews], [interviews]);

  const table = useReactTable({
    data: tableData,
    columns,
    defaultColumn: readOnlyColumn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const renderTableBodyRows = () => {
    if (props.isLoading) {
      return <TableSkeleton />;
    }

    // Empty state.
    if (!interviews.length) {
      const defaultEmptyText =
        "When interviews are completed, AI-generated summaries and transcripts will appear here.";
      const pendingEmptyText =
        "Pending interviews will appear here when candidates are invited or imported but haven't started the interview yet.";
      const searchEmptyText = "No matching interview transcripts found.";
      const scoreFilterEmptyText =
        "No interviews match the minimum score filter.";

      return (
        <tr>
          <td
            className="px-3 py-9 text-center text-sm text-foreground"
            colSpan={table.getAllColumns().length}
          >
            {interviewsParameters?.status_filter === "pending"
              ? pendingEmptyText
              : interviewsParameters?.min_assessment_score !== undefined
                ? scoreFilterEmptyText
                : interviewsParameters?.search_text
                  ? searchEmptyText
                  : defaultEmptyText}
          </td>
        </tr>
      );
    }

    return table.getRowModel().rows.map((row, index) => {
      // For recruiting projects we fix the width of the contact and assessment columns
      const cellIndexToStyle: Record<number, string> = {
        0: isRecruitingOrTrainingProject
          ? "left-0 w-[250px] min-w-[250px] max-w-[250px]"
          : "",
        1: isRecruitingOrTrainingProject
          ? "left-[250px] w-[180px] min-w-[140px] max-w-[180px] border-r border-border-light"
          : "",
      };

      return (
        <tr
          className={twMerge(
            "cursor-pointer hover:bg-blue-50",
            index % 2 === 0 ? "bg-white" : "bg-gray-50",
          )}
          key={row.id}
          onClick={() => {
            const interviewId = row.original.id;
            const matchingTranscriptFragmentId =
              interviewIdToMatchingFragmentId[interviewId];
            onInterviewClick(interviewId, matchingTranscriptFragmentId);
          }}
        >
          {row.getVisibleCells().map((cell, cellIndex) => {
            return (
              <td
                className={twMerge(
                  "text-left align-middle text-xs whitespace-nowrap text-foreground",
                  cellIndexToStyle[cellIndex] || "",
                  isRecruitingOrTrainingProject
                    ? "align-middle"
                    : "align-text-top",
                )}
                key={cell.id}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <div className="flex grow flex-col overflow-auto">
      {/* Container for the table that allows the body to scroll */}
      <div className="mx-auto mt-1 flex w-full flex-col overflow-auto rounded-t-md border-t border-border-light outline-hidden">
        <table className="isolate min-w-full border-x border-border-light outline-hidden">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    className={twMerge(
                      "sticky top-0 z-10 min-w-[120px] bg-gray-100 px-3 py-2 text-left align-top text-xs font-semibold break-words whitespace-normal",
                      index === 1 ? "border-r border-border-light" : "",
                    )}
                    colSpan={header.colSpan}
                    key={header.id}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "flex flex-col items-start cursor-pointer select-none"
                            : "flex flex-col items-start",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort()
                          ? getSortingChevron(
                              header.column.getIsSorted() as string,
                            )
                          : null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="overflow-y-auto bg-white">
            {renderTableBodyRows()}
          </tbody>
        </table>
      </div>
      <div className="mb-4 flex min-h-11 rounded-b-md border border-border-light bg-gray-100 px-3 py-2 text-sm">
        <PaginationControls
          onClickNextPage={() => {
            if (interviewsParameters) {
              dispatch(
                fetchNonEmptyInterviewsForProject({
                  ...interviewsParameters,
                  page: interviewsParameters.page + 1,
                }),
              );
            }
          }}
          onClickPreviousPage={() => {
            if (interviewsParameters) {
              dispatch(
                fetchNonEmptyInterviewsForProject({
                  ...interviewsParameters,
                  page: interviewsParameters.page - 1,
                }),
              );
            }
          }}
          page={interviewsParameters?.page || 1}
          totalPages={totalPages || 1}
        />
      </div>
    </div>
  );
};

function collectStandardAssessmentColumns(
  assessment: Assessment | undefined,
  interviewSectionDefinitions: InterviewSectionDefinition[] | undefined,
) {
  const assessmentColumns: ColumnDef<InterviewsTableRow>[] = [];

  // First, add special assessment columns for sections.
  for (const section of interviewSectionDefinitions ?? []) {
    switch (section.settings.type) {
      case InterviewSectionType.Questions:
        // Already shown in the other columns.
        break;

      case InterviewSectionType.RolePlay:
      case InterviewSectionType.KnowledgeTest: {
        const fallbackTitle =
          section.settings.type === InterviewSectionType.RolePlay
            ? "Role play"
            : "Knowledge test";
        const headerTitle = `${section.settings.title || fallbackTitle} assessment`;

        assessmentColumns.push({
          accessorFn: (row) => {
            const fieldName = buildInterviewSectionAssessmentCustomFieldName(
              section.id,
            );
            const field = row.custom_fields.find(
              (field) => field.field_name === fieldName,
            );

            if (!field) {
              return placeholder;
            }

            return (
              <InterviewAssessmentRating
                rating={field.data.value}
                fieldName={fieldName}
              />
            );
          },
          id: `section:${section.id}`,
          header: () => <span>{headerTitle}</span>,
          enableSorting: false,
        });
        break;
      }
      case InterviewSectionType.ChatQuiz:
        // TODO: Show chat quiz assessment result in the interviews table?
        break;

      default:
        assertUnreachable(section.settings, () => {});
        break;
    }
  }

  if (!assessment) {
    return assessmentColumns;
  }

  const sortedFields = sortAssessmentFields(
    assessment.custom_field_definitions,
  );

  // Then, add the standard assessment criteria columns
  sortedFields.forEach((field) => {
    assessmentColumns.push({
      accessorFn: (row) => {
        const customField = row.custom_fields.find(
          (cf) => cf.custom_field_definition_id === field.id,
        );

        if (row.status === "pending") {
          return placeholder;
        }

        return (
          <InterviewAssessmentRating
            rating={customField?.data.value}
            fieldName={customField?.field_name}
          />
        );
      },
      id: `assessment_${field.id}`,
      header: () => (
        <div
          className="line-clamp-4 flex flex-row items-center gap-2 overflow-hidden text-ellipsis"
          title={field.display_name}
        >
          {checkIsCustomFieldDefinitionImportanceMustHave(
            field.settings?.importance,
          ) ? (
            <TooltipTrigger>
              <TooltipHoverTarget shape="rounded">
                {renderCustomFieldDefinitionImportanceMustHaveIcon()}
              </TooltipHoverTarget>
              <SimpleTooltip>
                Qualification is a must have requirement
              </SimpleTooltip>
            </TooltipTrigger>
          ) : null}
          {field.display_name}
        </div>
      ),
      enableSorting: false,
    });
  });

  return assessmentColumns;
}

function getSortingChevron(sortDirection?: string) {
  switch (sortDirection) {
    case "asc":
      return (
        <span className="ml-2 h-fit flex-none rounded-sm bg-gray-200 text-foreground group-hover:bg-gray-200">
          <IconChevronUp aria-hidden="true" className="h-4 w-4" />
        </span>
      );
    case "desc":
      return (
        <span className="ml-2 h-fit flex-none rounded-sm bg-gray-200 text-foreground group-hover:bg-gray-200">
          <IconChevronDown aria-hidden="true" className="h-4 w-4" />
        </span>
      );
    default:
      return <div aria-hidden="true" className="ml-2 h-4 w-4" />; // Placeholder with the same size as the chevrons.
  }
}

const tooltipDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZoneName: "short",
};

function renderInterviewTimeIndicator(
  interview: InterviewWithContactAndActivities,
) {
  // Show when pending interviews were created.
  if (interview.status === "pending") {
    const formattedDateTime = new Date(interview.created_at).toLocaleString(
      "en-US",
      tooltipDateTimeFormatOptions,
    );

    return (
      <TooltipTrigger>
        <TooltipHoverTarget>
          <SmallTimeStamp timestamp={interview.created_at} />
        </TooltipHoverTarget>
        <SimpleTooltip>{`Added on ${formattedDateTime}`}</SimpleTooltip>
      </TooltipTrigger>
    );
  }

  // Show when active interviews were started.
  if (interview.started_at) {
    return <SmallTimeStamp timestamp={interview.started_at} />;
  }

  return null;
}

function renderInterviewStatusIndicator(
  interview: InterviewWithContactAndActivities,
  doesProjectHavePhoneNumber: boolean,
) {
  // Show if/when pending interviews were sent to the candidate.
  if (interview.status === "pending") {
    const outreach = computeInterviewOutreachInfo(
      interview,
      doesProjectHavePhoneNumber,
    );

    // This may happen between the time the interview is created and the first outreach message was sent.
    // TODO: Show an indicator for this? Do we know whether an outreach is going to occur?
    if (!outreach) {
      return null;
    }

    const channelLabel = outreach.channelType === "sms" ? "SMS" : "email";
    const formattedDateTime = new Date(outreach.timestamp).toLocaleString(
      "en-US",
      tooltipDateTimeFormatOptions,
    );

    return (
      <TooltipTrigger>
        <TooltipHoverTarget shape="rounded">
          {outreach.channelType === "sms" ? (
            <IconMessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <IconMail className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </TooltipHoverTarget>
        <SimpleTooltip>{`Invited via ${channelLabel} on ${formattedDateTime}`}</SimpleTooltip>
      </TooltipTrigger>
    );
  }

  // Show incomplete interviews.
  if (interview.status === "in_progress") {
    return (
      <TooltipTrigger>
        <TooltipHoverTarget shape="rounded">
          <IconClock className="h-4 w-4 shrink-0 text-muted-foreground" />
        </TooltipHoverTarget>
        <SimpleTooltip>Interview partially completed</SimpleTooltip>
      </TooltipTrigger>
    );
  }

  return null;
}

function computeInterviewOutreachInfo(
  interview: InterviewWithContactAndActivities,
  doesProjectHavePhoneNumber: boolean,
) {
  // Look up the first outreach activity.
  const activity = interview.activities?.find(
    (a) => a.type === "email_sent" || a.type === "outreach_message",
  );

  // Traditional email invitation.
  // TODO: Does this even happen for recruiting projects?
  if (activity?.type === "email_sent") {
    return {
      channelType: "email",
      timestamp: activity.created_at,
    };
  }

  // Automatic outreach.
  if (activity?.type === "outreach_message") {
    const doesContactHavePhoneNumber = !!interview.contact.phone_number;

    // Guess the channel type that was used.
    // TODO: Update the activity with metadata on how the message was _actually_ sent.
    const channelType =
      doesProjectHavePhoneNumber && doesContactHavePhoneNumber
        ? "sms"
        : "email";

    return {
      channelType,
      timestamp: activity.created_at,
    };
  }

  return undefined;
}

function maybeRenderUnansweredQuestionsIndicator(
  interview: InterviewWithContactAndActivities,
) {
  const unansweredQuestions =
    interview.insights?.unanswered_candidate_questions;
  if (!unansweredQuestions?.length) {
    return null;
  }

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">
        <IconHelpCircle className="h-4 w-4 shrink-0 text-yellow-600" />
      </TooltipHoverTarget>
      <SimpleTooltip>
        <UnansweredQuestionsBanner
          isTooltipMode
          unansweredQuestions={unansweredQuestions}
        />
      </SimpleTooltip>
    </TooltipTrigger>
  );
}
